import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { MapPin, TrendingUp, Lightbulb, Award } from 'lucide-react'

interface TimelineEvent {
  year: string
  title: string
  description: string
  icon: 'founding' | 'expansion' | 'innovation' | 'today'
}

const iconMap = {
  founding: MapPin,
  expansion: TrendingUp,
  innovation: Lightbulb,
  today: Award,
}

const iconBgMap = {
  founding: 'from-amber-500 to-orange-600',
  expansion: 'from-blue-500 to-indigo-600',
  innovation: 'from-purple-500 to-violet-600',
  today: 'from-emerald-500 to-teal-600',
}

const glowMap = {
  founding: 'bg-orange-500/20',
  expansion: 'bg-blue-500/20',
  innovation: 'bg-purple-500/20',
  today: 'bg-emerald-500/20',
}

export function TimelineSection() {
  const { t } = useTranslation('pages')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const events: TimelineEvent[] = [
    {
      year: '1988',
      title: t('chiSiamo.timeline.founding.title'),
      description: t('chiSiamo.timeline.founding.desc'),
      icon: 'founding',
    },
    {
      year: t('chiSiamo.timeline.expansion.year'),
      title: t('chiSiamo.timeline.expansion.title'),
      description: t('chiSiamo.timeline.expansion.desc'),
      icon: 'expansion',
    },
    {
      year: t('chiSiamo.timeline.innovation.year'),
      title: t('chiSiamo.timeline.innovation.title'),
      description: t('chiSiamo.timeline.innovation.desc'),
      icon: 'innovation',
    },
    {
      year: t('chiSiamo.timeline.today.year'),
      title: t('chiSiamo.timeline.today.title'),
      description: t('chiSiamo.timeline.today.desc'),
      icon: 'today',
    },
  ]

  return (
    <section ref={ref} className="py-16 md:py-24 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-[#f75e47]/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tl from-blue-500/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="mx-auto max-w-6xl px-4 relative">
        {/* Timeline container */}
        <div className="relative">
          {/* Central line - desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-300 to-transparent" />
            <motion.div
              className="absolute top-0 left-0 right-0 bg-gradient-to-b from-[#f75e47] via-purple-500 to-emerald-500"
              initial={{ height: '0%' }}
              animate={isInView ? { height: '100%' } : {}}
              transition={{ duration: 2, ease: 'easeOut' }}
            />
          </div>

          {/* Events */}
          <div className="space-y-12 lg:space-y-24">
            {events.map((event, index) => {
              const Icon = iconMap[event.icon]
              const isEven = index % 2 === 0

              return (
                <motion.div
                  key={event.year}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className={`relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-0 ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Content card */}
                  <div className={`flex-1 ${isEven ? 'lg:pr-16 lg:text-right' : 'lg:pl-16 lg:text-left'}`}>
                    <motion.div
                      whileHover={{ y: -4, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                      className={`group relative bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_8px_40px_-8px_rgba(0,0,0,0.05)] border border-slate-100 hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12),0_16px_50px_-12px_rgba(0,0,0,0.08)] transition-all duration-500 ${
                        isEven ? 'lg:ml-auto lg:mr-0' : 'lg:mr-auto lg:ml-0'
                      } max-w-lg`}
                    >
                      {/* Year badge */}
                      <div className={`flex items-center gap-3 mb-4 ${isEven ? 'lg:justify-end' : 'lg:justify-start'}`}>
                        <span className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-slate-900 text-white text-sm font-bold tracking-wide">
                          {event.year}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 group-hover:text-[#f75e47] transition-colors duration-300">
                        {event.title}
                      </h3>

                      {/* Description */}
                      <p className="text-slate-600 leading-relaxed">
                        {event.description}
                      </p>

                      {/* Decorative corner */}
                      <div className={`absolute top-0 w-16 h-16 overflow-hidden rounded-tl-2xl ${isEven ? 'right-0 rounded-tr-2xl rounded-tl-none' : 'left-0 rounded-tl-2xl'}`}>
                        <div className={`absolute top-0 ${isEven ? 'right-0' : 'left-0'} w-32 h-32 bg-gradient-to-br ${iconBgMap[event.icon]} opacity-10 transform rotate-45 translate-x-8 -translate-y-8`} />
                      </div>

                    </motion.div>
                  </div>

                  {/* Center icon - desktop */}
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 z-10">
                    <motion.div
                      whileHover={{ scale: 1.15, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className="relative"
                    >
                      {/* Glow effect */}
                      <div className={`absolute inset-0 rounded-full ${glowMap[event.icon]} blur-xl scale-150`} />
                      {/* Icon circle */}
                      <div className={`relative w-16 h-16 rounded-full bg-gradient-to-br ${iconBgMap[event.icon]} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-7 h-7 text-white" strokeWidth={2} />
                      </div>
                      {/* Ring */}
                      <div className="absolute inset-0 rounded-full border-2 border-white/30 scale-110" />
                    </motion.div>
                  </div>

                  {/* Mobile icon */}
                  <div className="lg:hidden absolute left-0 top-6 -translate-x-1/2 z-10">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${iconBgMap[event.icon]} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                  </div>

                  {/* Empty space for alternating layout */}
                  <div className="hidden lg:block flex-1" />
                </motion.div>
              )
            })}
          </div>

          {/* Bottom decoration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 1 }}
            className="hidden lg:flex absolute left-1/2 -translate-x-1/2 -bottom-8"
          >
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg ring-4 ring-white" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}
